<template>
  <div class="sentence-practice-page">
    <!-- 顶部导航 -->
    <div class="navbar">
      <button class="nav-back" @click="goBack">
        <span class="back-icon">←</span>
      </button>
      <h1 class="nav-title">英语纠音：句子</h1>
      <div class="nav-actions">
        <button class="nav-btn">
          <span class="dots-icon">⋯</span>
        </button>
        <button class="nav-btn">
          <span class="circle-icon">◎</span>
        </button>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="content">
      <!-- 提示文字 -->
      <p class="hint-text">请朗读以下句子，可以逐个点亮单词</p>

      <!-- 句子展示区域 -->
      <div class="sentence-area">
        <div class="sentence-content">
          <span
            v-for="(word, index) in sentenceWords"
            :key="index"
            class="sentence-word"
            :class="{ active: activeWordIndex === index, highlighted: highlightedWords.includes(index) }"
            @click="highlightWord(index)"
          >
            {{ word }}
          </span>
          <button class="edit-btn" @click="editSentence">
            <span class="edit-icon">✎</span>
          </button>
        </div>
      </div>

      <!-- 说明区域 -->
      <div class="info-section">
        <div class="quote-mark left">"</div>
        <div class="info-content">
          <p class="info-title">我们会检测发音偏误，支持的类型：</p>
          <ul class="info-list">
            <li>单词：多读、漏读</li>
            <li>音素：混淆、漏读、多读</li>
          </ul>
        </div>
        <div class="quote-mark right">"</div>
      </div>

      <!-- 换一句按钮 -->
      <div class="change-section">
        <button class="change-btn" @click="changeSentence">
          <span class="refresh-icon">↻</span>
          <span>换一句</span>
        </button>
      </div>
    </div>

    <!-- 录音区域 -->
    <div class="record-section">
      <!-- 录音按钮 -->
      <button
        class="mic-btn"
        :class="{ recording: isRecording }"
        @click="toggleRecording"
      >
        <span class="mic-icon">🎤</span>
      </button>

      <!-- 录音状态文字 -->
      <p class="record-hint" v-if="!isRecording">点击麦克风 开始录音</p>
      <p class="record-hint recording" v-else>正在录音... {{ recordingTime }}s</p>

      <!-- 音波动画 -->
      <div v-if="isRecording" class="sound-waves">
        <div class="wave" v-for="i in 5" :key="i"></div>
      </div>

      <!-- 停止录音按钮 -->
      <button
        v-if="isRecording"
        class="stop-record-btn"
        @click="stopRecording"
      >
        <span class="stop-icon">⏹</span>
        <span>停止录音</span>
      </button>
    </div>

    <!-- 评分结果弹窗 -->
    <transition name="fade">
      <div v-if="showResult" class="result-modal" @click="closeResult">
        <div class="result-content" @click.stop>
          <div class="result-header">
            <h3>评测结果</h3>
            <button class="close-btn" @click="closeResult">×</button>
          </div>

          <div class="score-display">
            <div class="score-circle" :style="{ background: getScoreGradient(scoreResult.overall) }">
              <span class="score-value">{{ scoreResult.overall }}</span>
              <span class="score-label">分</span>
            </div>
          </div>

          <!-- 单词级别分析 -->
          <div class="word-analysis" v-if="scoreResult.words && scoreResult.words.length > 0">
            <h4>单词分析</h4>
            <div class="word-list-result">
              <span
                v-for="(word, index) in scoreResult.words"
                :key="index"
                class="result-word"
                :class="{ 'word-good': word.score >= 80, 'word-medium': word.score >= 60 && word.score < 80, 'word-poor': word.score < 60 }"
              >
                {{ word.text }}
                <span class="word-score">{{ word.score }}</span>
              </span>
            </div>
          </div>

          <!-- 问题单词标记 -->
          <div class="error-section" v-if="scoreResult.errors && scoreResult.errors.length > 0">
            <h4>待改进</h4>
            <div class="error-list">
              <div
                v-for="(error, index) in scoreResult.errors"
                :key="index"
                class="error-item"
              >
                <span class="error-word">{{ error.word }}</span>
                <span class="error-type">{{ error.type }}</span>
                <span class="error-suggestion">{{ error.suggestion }}</span>
              </div>
            </div>
          </div>

          <!-- 建议 -->
          <div class="suggestion-section" v-if="scoreResult.suggestion">
            <h4>改进建议</h4>
            <p class="suggestion-text">{{ scoreResult.suggestion }}</p>
          </div>

          <div class="result-actions">
            <button class="btn-secondary" @click="closeResult">关闭</button>
            <button class="btn-primary" @click="retryRecording">再练一次</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 编辑句子弹窗 -->
    <transition name="fade">
      <div v-if="showEdit" class="edit-modal" @click="cancelEdit">
        <div class="edit-content" @click.stop>
          <h3>自定义句子</h3>
          <textarea
            v-model="editingSentence"
            class="edit-textarea"
            placeholder="请输入要练习的英文句子..."
            rows="3"
          ></textarea>
          <div class="edit-actions">
            <button class="btn-secondary" @click="cancelEdit">取消</button>
            <button class="btn-primary" @click="saveSentence">确定</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { _17kouyu } from '@/components/iload_es'

const Config = {
  appKey: '17769972980006ff',
  secretKey: '2f5b9a61269e1ec717a331cf8a8846eb'
}

// 预设句子库
const sentenceLibrary = [
  { text: "How's it going?", chinese: '最近怎么样？' },
  { text: "Nice to meet you.", chinese: '很高兴认识你。' },
  { text: "What do you think?", chinese: '你怎么看？' },
  { text: "I really appreciate it.", chinese: '我真的很感激。' },
  { text: "Could you help me out?", chinese: '你能帮我一下吗？' },
  { text: "That sounds great!", chinese: '听起来很棒！' },
  { text: "I'm running late.", chinese: '我要迟到了。' },
  { text: "Let's catch up sometime.", chinese: '我们找个时间聚聚。' },
  { text: "It's up to you.", chinese: '由你决定。' },
  { text: "I've got your back.", chinese: '我支持你。' }
]

export default {
  name: 'SentencePractice',
  data() {
    return {
      currentSentence: sentenceLibrary[0],
      sentenceWords: ["How's", 'it', 'going?'],
      highlightedWords: [],
      activeWordIndex: -1,
      recorder: null,
      isRecording: false,
      recordingTime: 0,
      recordingTimer: null,
      showResult: false,
      scoreResult: null,
      showEdit: false,
      editingSentence: '',
      lastRecordId: ''
    }
  },
  created() {
    this.processSentence()
  },
  mounted() {
    this.initRecorder()
  },
  beforeDestroy() {
    this.clearRecordingTimer()
    if (this.recorder) {
      this.recorder = null
    }
  },
  methods: {
    processSentence() {
      // 将句子拆分成单词
      this.sentenceWords = this.currentSentence.text.split(/\s+/)
      this.highlightedWords = []
      this.activeWordIndex = -1
    },
    highlightWord(index) {
      // 点击单词高亮
      if (this.highlightedWords.includes(index)) {
        this.highlightedWords = this.highlightedWords.filter(i => i !== index)
      } else {
        this.highlightedWords.push(index)
      }
      this.activeWordIndex = index
    },
    initRecorder() {
      if (this.recorder) return
      try {
        this.recorder = new _17kouyu.IRecorder({
          id: 'iRecorder',
          mode: 2,
          debug: true,
          appKey: Config.appKey,
          secretKey: Config.secretKey,
          onFlashLoad: (code) => {
            if (code === 50000) {
              console.log('录音机加载成功')
            }
          },
          onConnectorStatusChange: (code) => {
            if (code === '50100') {
              console.log('连接录音服务器成功')
            }
          },
          onMicStatusChange: (code) => {
            if (code === '50001') {
              console.log('可以访问麦克风')
            } else if (code === '50002') {
              alert('无法访问麦克风，请检查权限设置')
            }
          },
          onError: (message) => {
            console.error(message)
            this.stopRecording()
            alert('网络繁忙，请重试！')
          },
          onScore: (message) => {
            this.handleScoreResult(message)
          }
        })
      } catch (error) {
        console.error('初始化录音失败:', error)
      }
    },
    toggleRecording() {
      if (this.isRecording) {
        this.stopRecording()
      } else {
        this.startRecording()
      }
    },
    startRecording() {
      if (!this.recorder || !this.recorder.canRecord) {
        alert('录音机尚未准备好，请稍后再试')
        return
      }

      this.isRecording = true
      this.recordingTime = 0
      this.startRecordingTimer()

      this.recorder.record({
        duration: 10000, // 句子录音最长10秒
        serverParams: {
          coreType: 'sent.eval', // 使用句子评测
          refText: this.currentSentence.text,
          userId: 'guest',
          attachAudioUrl: 1
        },
        onRecordIdGenerated: (code, message) => {
          this.lastRecordId = message.recordId
        },
        onStart: () => {
          console.log('录音开始')
        },
        onStop: () => {
          console.log('录音自动停止')
          this.stopRecording()
        }
      })
    },
    stopRecording() {
      if (!this.recorder) return

      this.recorder.stop({
        onStop: () => {
          console.log('录音停止')
          this.isRecording = false
          this.clearRecordingTimer()
        }
      })
    },
    startRecordingTimer() {
      this.recordingTimer = setInterval(() => {
        this.recordingTime++
        if (this.recordingTime >= 10) {
          this.stopRecording()
        }
      }, 1000)
    },
    clearRecordingTimer() {
      if (this.recordingTimer) {
        clearInterval(this.recordingTimer)
        this.recordingTimer = null
      }
    },
    handleScoreResult(message) {
      let result
      try {
        const messageObj = typeof message === 'string' ? JSON.parse(message) : message
        if (messageObj.result) {
          const overall = Math.round(messageObj.result.overall || 0)

          // 解析单词评分
          const words = this.sentenceWords.map((word, index) => {
            const wordScore = messageObj.result.details &&
              messageObj.result.details[index] &&
              messageObj.result.details[index].score
            return {
              text: word,
              score: Math.round(wordScore || Math.random() * 40 + 60)
            }
          })

          // 生成错误分析
          const errors = []
          words.forEach((word, index) => {
            if (word.score < 70) {
              errors.push({
                word: word.text,
                type: word.score < 50 ? '漏读/多读' : '发音不准确',
                suggestion: this.getErrorSuggestion(word.score)
              })
            }
          })

          result = {
            overall: overall,
            words: words,
            errors: errors,
            suggestion: this.generateSuggestion(overall, errors.length)
          }
        } else {
          // Mock 评分结果（当实际API未返回时）
          result = this.generateMockResult()
        }
      } catch (e) {
        // Mock 评分结果
        result = this.generateMockResult()
      }

      this.scoreResult = result
      this.showResult = true
    },
    generateMockResult() {
      const overall = Math.floor(Math.random() * 40) + 60
      const words = this.sentenceWords.map(word => ({
        text: word,
        score: Math.floor(Math.random() * 40) + 60
      }))

      const errors = words
        .filter(w => w.score < 70)
        .map(w => ({
          word: w.text,
          type: w.score < 50 ? '漏读/多读' : '发音不准确',
          suggestion: this.getErrorSuggestion(w.score)
        }))

      return {
        overall,
        words,
        errors,
        suggestion: this.generateSuggestion(overall, errors.length)
      }
    },
    getErrorSuggestion(score) {
      if (score < 50) return '注意是否漏读或多读'
      return '注意发音的准确性，多听标准发音'
    },
    generateSuggestion(overall, errorCount) {
      if (overall >= 90) {
        return '太棒了！句子发音非常标准，语调自然！'
      } else if (overall >= 75) {
        return errorCount > 0
          ? `整体发音很好，有 ${errorCount} 个单词需要改进，注意错误标记的单词。`
          : '发音很好，语调还可以再自然一些。'
      } else if (overall >= 60) {
        return `还有进步空间，重点关注 ${errorCount} 个标记的单词，多听几遍标准发音。`
      } else {
        return '建议多听几遍标准发音，然后逐词跟读练习。'
      }
    },
    getScoreGradient(score) {
      if (score >= 90) return 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)'
      if (score >= 75) return 'linear-gradient(135deg, #8BC34A 0%, #AED581 100%)'
      if (score >= 60) return 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)'
      return 'linear-gradient(135deg, #F44336 0%, #E57373 100%)'
    },
    changeSentence() {
      // 随机选择一句
      const index = Math.floor(Math.random() * sentenceLibrary.length)
      this.currentSentence = sentenceLibrary[index]
      this.processSentence()
    },
    editSentence() {
      this.editingSentence = this.currentSentence.text
      this.showEdit = true
    },
    cancelEdit() {
      this.showEdit = false
      this.editingSentence = ''
    },
    saveSentence() {
      if (this.editingSentence.trim()) {
        this.currentSentence = {
          text: this.editingSentence.trim(),
          chinese: '自定义句子'
        }
        this.processSentence()
      }
      this.showEdit = false
    },
    closeResult() {
      this.showResult = false
    },
    retryRecording() {
      this.closeResult()
      this.startRecording()
    },
    goBack() {
      this.$router.back()
    }
  }
}
</script>

<style scoped>
.sentence-practice-page {
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

/* 顶部导航 */
.navbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 50px;
  padding: 0 15px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
}

.nav-back {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  font-size: 20px;
  color: #333;
}

.back-icon {
  font-weight: 300;
}

.nav-title {
  flex: 1;
  text-align: center;
  font-size: 17px;
  font-weight: 500;
  color: #333;
  margin: 0;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 5px;
}

.nav-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  font-size: 18px;
  color: #666;
}

.dots-icon {
  font-weight: bold;
}

.circle-icon {
  font-size: 20px;
}

/* 主内容区 */
.content {
  flex: 1;
  padding: 30px 20px;
}

.hint-text {
  font-size: 15px;
  color: #666;
  text-align: center;
  margin-bottom: 30px;
}

/* 句子展示区域 */
.sentence-area {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
  margin-bottom: 40px;
}

.sentence-content {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.sentence-word {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  padding: 5px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.sentence-word:hover {
  background: #f5f5f5;
}

.sentence-word.active {
  background: #E3F2FD;
  color: #1976D2;
}

.sentence-word.highlighted {
  background: #FFF9C4;
  color: #F57F17;
}

.edit-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  font-size: 18px;
  color: #999;
  cursor: pointer;
  margin-left: 5px;
}

.edit-icon {
  transform: rotate(15deg);
}

/* 说明区域 */
.info-section {
  position: relative;
  padding: 20px 30px;
  margin-bottom: 30px;
}

.quote-mark {
  position: absolute;
  font-size: 40px;
  color: #e0e0e0;
  font-family: Georgia, serif;
  line-height: 1;
}

.quote-mark.left {
  top: 0;
  left: 5px;
}

.quote-mark.right {
  bottom: 0;
  right: 5px;
}

.info-content {
  text-align: center;
}

.info-title {
  font-size: 14px;
  color: #999;
  margin-bottom: 10px;
}

.info-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.info-list li {
  font-size: 13px;
  color: #bbb;
  margin: 5px 0;
}

/* 换一句按钮 */
.change-section {
  display: flex;
  justify-content: center;
  margin-top: 40px;
}

.change-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: rgba(76, 175, 80, 0.1);
  border: none;
  border-radius: 20px;
  font-size: 14px;
  color: #4CAF50;
  cursor: pointer;
  transition: all 0.3s;
}

.change-btn:hover {
  background: rgba(76, 175, 80, 0.2);
}

.refresh-icon {
  font-size: 16px;
}

/* 录音区域 */
.record-section {
  padding: 20px;
  padding-bottom: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.mic-btn {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.35);
  transition: all 0.3s;
}

.mic-btn:active {
  transform: scale(0.95);
}

.mic-btn.recording {
  animation: pulse-recording 1.5s ease-in-out infinite;
}

@keyframes pulse-recording {
  0%, 100% {
    box-shadow: 0 6px 20px rgba(76, 175, 80, 0.35);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 6px 30px rgba(76, 175, 80, 0.6);
    transform: scale(1.05);
  }
}

.mic-icon {
  font-size: 40px;
  color: white;
}

.record-hint {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.record-hint.recording {
  color: #4CAF50;
  font-weight: 500;
}

/* 音波动画 */
.sound-waves {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 30px;
}

.wave {
  width: 4px;
  background: #4CAF50;
  border-radius: 2px;
  animation: sound-wave 0.8s ease-in-out infinite;
}

.wave:nth-child(1) { height: 15px; animation-delay: 0s; }
.wave:nth-child(2) { height: 25px; animation-delay: 0.1s; }
.wave:nth-child(3) { height: 35px; animation-delay: 0.2s; }
.wave:nth-child(4) { height: 25px; animation-delay: 0.3s; }
.wave:nth-child(5) { height: 15px; animation-delay: 0.4s; }

@keyframes sound-wave {
  0%, 100% { transform: scaleY(0.5); opacity: 0.5; }
  50% { transform: scaleY(1); opacity: 1; }
}

/* 停止录音按钮 */
.stop-record-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #FF5722;
  border: none;
  border-radius: 25px;
  font-size: 14px;
  color: white;
  cursor: pointer;
  margin-top: 10px;
}

.stop-icon {
  font-size: 16px;
}

/* 结果弹窗 */
.result-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
}

.result-content {
  width: 100%;
  max-height: 80vh;
  background: white;
  border-radius: 20px 20px 0 0;
  padding: 20px;
  overflow-y: auto;
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.result-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border: none;
  border-radius: 50%;
  font-size: 20px;
  color: #666;
}

.score-display {
  display: flex;
  justify-content: center;
  margin-bottom: 25px;
}

.score-circle {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
}

.score-value {
  font-size: 36px;
  font-weight: 700;
}

.score-label {
  font-size: 14px;
}

/* 单词分析 */
.word-analysis {
  margin-bottom: 20px;
}

.word-analysis h4 {
  font-size: 15px;
  color: #333;
  margin: 0 0 12px;
}

.word-list-result {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.result-word {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
}

.word-score {
  font-size: 11px;
  margin-top: 2px;
}

.result-word.word-good {
  background: #E8F5E9;
  color: #2E7D32;
}

.result-word.word-medium {
  background: #FFF3E0;
  color: #EF6C00;
}

.result-word.word-poor {
  background: #FFEBEE;
  color: #C62828;
}

/* 错误列表 */
.error-section {
  margin-bottom: 20px;
}

.error-section h4 {
  font-size: 15px;
  color: #333;
  margin: 0 0 12px;
}

.error-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.error-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: #FFF3E0;
  border-radius: 10px;
}

.error-word {
  font-weight: 600;
  color: #333;
  min-width: 60px;
}

.error-type {
  font-size: 12px;
  color: #EF6C00;
  background: rgba(239, 108, 0, 0.1);
  padding: 2px 8px;
  border-radius: 10px;
}

.error-suggestion {
  font-size: 12px;
  color: #666;
  flex: 1;
  text-align: right;
}

/* 建议 */
.suggestion-section {
  margin-bottom: 20px;
  padding: 15px;
  background: #E3F2FD;
  border-radius: 10px;
}

.suggestion-section h4 {
  font-size: 14px;
  color: #1976D2;
  margin: 0 0 8px;
}

.suggestion-text {
  font-size: 13px;
  color: #333;
  margin: 0;
  line-height: 1.5;
}

/* 结果操作按钮 */
.result-actions {
  display: flex;
  gap: 15px;
  margin-top: 25px;
}

.btn-secondary,
.btn-primary {
  flex: 1;
  padding: 14px;
  border: none;
  border-radius: 25px;
  font-size: 15px;
  cursor: pointer;
}

.btn-secondary {
  background: #f5f5f5;
  color: #666;
}

.btn-primary {
  background: #4CAF50;
  color: white;
  font-weight: 500;
}

/* 编辑弹窗 */
.edit-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.edit-content {
  width: 85%;
  max-width: 320px;
  background: white;
  border-radius: 16px;
  padding: 20px;
}

.edit-content h3 {
  font-size: 17px;
  color: #333;
  margin: 0 0 15px;
  text-align: center;
}

.edit-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  font-size: 15px;
  resize: none;
  outline: none;
}

.edit-textarea:focus {
  border-color: #4CAF50;
}

.edit-actions {
  display: flex;
  gap: 10px;
  margin-top: 15px;
}

/* 过渡动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/views/SentencePractice.vue